import { Component, ElementRef, Inject, Input, OnInit, ViewChild, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TemperatureDerationService } from '../shared/services/temperature-deration.service';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { TemperatureDerationCatalog } from '../models/temperature-deration-catalog.model';
import { CatalogScope, CatalogFilterMode } from '../models/catalog-scope.enum';
import { EMPTY_GUID } from 'src/app/perical/models/thermal-operation.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { GetValueFromFahrenheit } from 'src/app/perivis/shared/helpers/units.helper';
import { LinearInterpSingleVal } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import {
  createNewTemperatureDerationProfile,
  createTempDerationColumns,
  createTempDerationRow,
  ensureTrailingInputRow,
  extractTempDerationRows,
  populateTempDerationFormArray,
  TemperatureDerationPoint
} from './helpers/temperature-deration.helper';
import {
  buildTemperatureDerationFingerprint,
  buildTemperatureDerationFingerprintMap,
  findTemperatureDerationIndex,
  getTemperatureDerationProfileKey,
  hasDuplicateTemperatureDerationName,
  upsertSavedTemperatureDerationFingerprint
} from './helpers/temperature-deration-save.helper';

@Component({
  selector: 'app-temperature-deration',
  templateUrl: './temperature-deration.component.html',
  styleUrls: ['./temperature-deration.component.scss'],
  providers: [ConfirmationService],
  standalone: false
})
export class TemperatureDerationComponent implements OnInit {
  @ViewChild('nameField') nameField!: ElementRef<HTMLInputElement>;
  tablePlotSelected = 'table';
  modeSelected = 'single';
  tablePlotOptions = [
    { label: 'Table', value: 'table' },
    { label: 'Plot', value: 'plot' }
  ];
  modes: SelectItem[] = [
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];

  downloadPlotName = 'temperature-deration-plot';

  tempDerations: TemperatureDerationCatalog[] = [];
  selectedTempDeration = signal<Partial<TemperatureDerationCatalog> | null>(null);
  isLoading = signal<boolean>(false);
  userUnits = signal<UserUnitsModel | null>(null);
  interpolatedValue: number | null = null;
  temperatureUnit = '';
  interpolationTemperatureControl = new FormControl<number | null>(null);
  readonly interpolationValidation = { required: false };

  readonly tempDerationColumns = createTempDerationColumns();
  tempDerationRows = new FormArray<FormGroup>([]);
  createTempDerationRow = createTempDerationRow;

  CatalogScope = CatalogScope;
  selectedScopeFilter: CatalogFilterMode = CatalogFilterMode.AllItems;
  userRoles: UserRoles | null = null;

  private _userUnits: UserUnitsModel = new UserUnitsModel();
  private saveInFlight = signal(false);
  private queuedSaveIndex = signal<number | null>(null);
  private lastSavedFingerprints = signal<Record<string, string>>({});

  constructor(
    @Inject(TemperatureDerationService) private _temperatureDerationService: TemperatureDerationService,
    @Inject(ConfirmationService) private _confirmationService: ConfirmationService,
    @Inject(StoreService) private _storeService: StoreService,
    @Inject(AppNotificationService) private _toaster: AppNotificationService,
    @Inject(MediatorService) private _messenger: MediatorService
  ) { }

  // State
  @Input()
  componentId = '';

  async ngOnInit(): Promise<void> {
    this.userRoles = (await this._storeService.get<UserRoles>(StorageKeys.ROLES)) ?? null;
    this._userUnits = (await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS)) ?? new UserUnitsModel();
    this.userUnits.set(this._userUnits);

    this.temperatureUnit = this._userUnits.temperature;

    this.getData();
  }

  getData() {
    this._temperatureDerationService.getTemperatureDerations(this.selectedScopeFilter).subscribe(tempDerations => {
      this.tempDerations = tempDerations;

      const defaultItem = this.tempDerations.find((x) => x.name === 'Default');
      if (defaultItem) {
          this.tempDerations.splice(this.tempDerations.indexOf(defaultItem), 1);
          this.tempDerations.unshift(defaultItem);
      }

      this.hydrateSavedFingerprints();
      this.setSelectedProfile(this.tempDerations[0]);
    });
  }

  onScopeFilterChange(): void {
    this.getData();
  }

  addItem(): void {
    if (this.tempDerations.length > 0 && this.tempDerations[this.tempDerations.length - 1].id === EMPTY_GUID) {
      return;
    }

    this.nameField.nativeElement.focus();
    const defaultTemperature = GetValueFromFahrenheit(75, this._userUnits.temperature);
    const tempDeration = createNewTemperatureDerationProfile(defaultTemperature, EMPTY_GUID);

    this.tempDerations.push(tempDeration);
    this.setSelectedProfile(this.tempDerations[this.tempDerations.length - 1]);
  }

  onSelect(e: { option?: Partial<TemperatureDerationCatalog> | null }): void {
    if (e?.option) {
      this.setSelectedProfile(e.option);
      this.modeSelected = 'single';
    }
  }

  deleteProfileCheck(item: TemperatureDerationCatalog): boolean {
    const isCatalogAdmin = !!this.userRoles?.catalogAdmin;
    return item.isLocked || (!isCatalogAdmin && item.scope !== CatalogScope.User);
  }

  deleteProfile(tempDeration: TemperatureDerationCatalog): void {
    this.isLoading.set(true);
    if (tempDeration.id === EMPTY_GUID) {
      this.selectTemperatureDerationAfterDelete(tempDeration);
      return;
    }
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete ' + tempDeration.name.bold() + '?',
      accept: () => {
        this._temperatureDerationService.deleteTempereatureDeration(tempDeration.id).subscribe(() => {
          this._messenger.publish(new DataSavedMessage('Temperature Deration'));
          this.selectTemperatureDerationAfterDelete(tempDeration);
        });
      }
    });
    this.isLoading.set(false);
  }

  onTempDerationRowDeleted(index: number): void {
    if (index >= 0 && index < this.tempDerationRows.length) {
      this.tempDerationRows.removeAt(index);
    }
    this.onTemperatureDerationGridChange();
  }

  onTemperatureDerationGridChange(): void {
    ensureTrailingInputRow(this.tempDerationRows);
    const dataRows = extractTempDerationRows(this.tempDerationRows);
    const selected = this.selectedTempDeration();
    if (!selected) return;

    const index = this.findSelectedIndex(selected);
    if (index === -1) return;

    this.tempDerations[index].temperatureDerationProfile = [...dataRows];
    this.queueSaveForIndex(index);
  }

  onNameChange(e: Event): void {
    const target = e.target as HTMLInputElement | null;
    const name = target?.value ?? '';
    if (name.length === 0) {
      return;
    }

    const selected = this.selectedTempDeration();
    const index = this.findSelectedIndex(selected);
    if (index === -1) return;

    this.tempDerations[index].name = name;
    this.queueSaveForIndex(index);
  }

  interpolate(e: { value: number | null }): void {
    const profile: TemperatureDerationPoint[] = this.selectedTempDeration()?.temperatureDerationProfile ?? [];
    if (profile.length < 2) return;

    const temps = profile.map((p) => p.temperature);
    const factors = profile.map((p) => p.derationFactor);
    const inputTemp = e.value;

    if (inputTemp == null || Number.isNaN(inputTemp)) {
      this.interpolatedValue = null;
      return;
    }

    if (inputTemp <= temps[0]) {
      this.interpolatedValue = factors[0];
      return;
    }
    if (inputTemp >= temps[temps.length - 1]) {
      this.interpolatedValue = factors[temps.length - 1];
      return;
    }
    this.interpolatedValue = LinearInterpSingleVal(inputTemp, temps, factors);
  }

  getScopeLabel(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'Global' : 'User';
  }

  getScopeBadgeClass(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  }

  tablePlotToggle(): void {
    if (this.tablePlotSelected !== 'plot') {
      this.modeSelected = 'single';
    }
  }

  async toggleTempDerationScope(): Promise<void> {
    const current = this.selectedTempDeration();
    if (!current || this.saveInFlight()) {
      return;
    }

    const index = this.findSelectedIndex(current);
    if (index < 0) {
      return;
    }

    const currentScope = current.scope;
    const newScope = currentScope === CatalogScope.User ? CatalogScope.Global : CatalogScope.User;
    this.tempDerations[index].scope = newScope;
    this.selectedTempDeration.update(s => s ? ({ ...s, scope: newScope }) : s);

    this.queueSaveForIndex(index);
  }

  private setSelectedProfile(profile: Partial<TemperatureDerationCatalog> | null): void {
    this.selectedTempDeration.set(profile);
    const profilePoints = profile?.temperatureDerationProfile || [];
    populateTempDerationFormArray(this.tempDerationRows, profilePoints);
  }

  private selectTemperatureDerationAfterDelete(item: TemperatureDerationCatalog): void {
    const index = this.tempDerations.findIndex((x) => x.id === item.id);
    this.tempDerations.splice(index, 1);
    this.setSelectedProfile(this.tempDerations[this.tempDerations.length - 1] ?? null);
    this.isLoading.set(false);
  }

  private findSelectedIndex(selected: Partial<TemperatureDerationCatalog> | null): number {
    return findTemperatureDerationIndex(this.tempDerations, selected);
  }

  private queueSaveForIndex(index: number): void {
    if (index < 0 || index >= this.tempDerations.length) {
      return;
    }

    this.queuedSaveIndex.set(index);

    if (this.saveInFlight()) {
      return;
    }

    this.processQueuedSave();
  }

  private processQueuedSave(): void {
    const queuedIndex = this.queuedSaveIndex();
    if (queuedIndex === null) {
      return;
    }

    this.queuedSaveIndex.set(null);

    if (!this.shouldPersistIndex(queuedIndex)) {
      if (this.queuedSaveIndex() !== null) {
        this.processQueuedSave();
      }
      return;
    }

    this.persistTemperatureDeration(queuedIndex);
  }

  private shouldPersistIndex(index: number): boolean {
    const item = this.tempDerations[index];
    if (!item || item.isLocked) {
      return false;
    }

    const name = item.name?.trim();
    if (!name) {
      return false;
    }

    if (!Array.isArray(item.temperatureDerationProfile) || item.temperatureDerationProfile.length === 0) {
      return false;
    }

    if (hasDuplicateTemperatureDerationName(this.tempDerations, index)) {
      this._toaster.showError(`Profile with name ${name} already exists`);
      return false;
    }

    const currentFingerprint = buildTemperatureDerationFingerprint(item);
    const key = getTemperatureDerationProfileKey(item, index, EMPTY_GUID);
    const previousFingerprint = this.lastSavedFingerprints()[key];

    return previousFingerprint !== currentFingerprint;
  }

  private persistTemperatureDeration(index: number): void {
    const item = this.tempDerations[index];
    if (!item) {
      return;
    }

    const previousKey = getTemperatureDerationProfileKey(item, index, EMPTY_GUID);
    this.saveInFlight.set(true);

    if (item.id === EMPTY_GUID) {
      this.isLoading.set(true);
      this._temperatureDerationService.createTemperatureDerations(item).subscribe({
        next: (res) => {
          this.tempDerations[index].id = res;
          this.markAsSaved(index, previousKey);
          this._messenger.publish(new DataSavedMessage('Temperature Deration'));
          this.saveInFlight.set(false);
          this.isLoading.set(false);
          this.processQueuedSave();
        },
        error: () => {
          this.saveInFlight.set(false);
          this.isLoading.set(false);
          this.processQueuedSave();
        }
      });

      return;
    }

    this._temperatureDerationService.updateTemperatureDeration(item).subscribe({
      next: () => {
        this.markAsSaved(index);
        this._messenger.publish(new DataSavedMessage('Temperature Deration'));
        this.saveInFlight.set(false);
        this.processQueuedSave();
      },
      error: () => {
        this.saveInFlight.set(false);
        this.processQueuedSave();
      }
    });
  }

  private hydrateSavedFingerprints(): void {
    this.lastSavedFingerprints.set(buildTemperatureDerationFingerprintMap(this.tempDerations, EMPTY_GUID));
  }

  private markAsSaved(index: number, previousKey?: string): void {
    const item = this.tempDerations[index];
    if (!item) {
      return;
    }

    this.lastSavedFingerprints.set(
      upsertSavedTemperatureDerationFingerprint(
        this.lastSavedFingerprints(),
        item,
        index,
        EMPTY_GUID,
        previousKey
      )
    );
  }
}
